// The customer's meeting: the property is the screen. The sales manager leads the tour and the canvas follows him —
// or the customer wanders off (pinch a plan, drag the walk-through, step through the story) and he sees where. His
// video is a small window she can put full screen; Book, Cost sheet and Ask are always there.
import { RoomEvent, Track } from "livekit-client";
import { bookVisit, postEvents } from "./api.js";
import { firstName, h, initials, toast } from "./dom.js";
import { icon } from "./icons.js";
import { applyView, canvasHTML, docAnchor, docSpot, fitPlans, isPage, paintPointer, paintYaw, roomOf, sceneOf, spotsOf, thumbOf, variantOf, openSlots } from "./canvas/render.js";
import { LANGS, pickLang, t } from "./canvas/i18n.js";
import { decode, makeSender, makeTextSender, roleOf, throttle, TOPIC } from "./canvas/sync.js";
import { customerPrivacy } from "./canvas/privacy.js";
import { meetingWords } from "./host-words.js";
import { mountPano, panoIn } from "./canvas/pano.js";
import { allowedDeletes, cleanStroke, colourOf, InkBook, inkId, LIMITS, mayDraw, pageKey, pageOk, paintDocPeers, paintInk, paintPeers, peerFrom, pointerPage, readCapped, unitsAt } from "./canvas/ink.js";

const QUESTIONS = ["When is possession?", "Which floors are available?", "Can I get a home loan?", "Can I see the other unit type?", "How much parking comes with it?"];

export function runCustomer(root, { code, info, canvas, session, lang = "en" }) {
  const { room } = session;
  const send = makeSender(room);
  const sendText = makeTextSender(room);
  const hostName = info.meeting.hostName;
  const hostFirst = firstName(hostName);
  const words = meetingWords(info.meeting);
  // g-site-privacy (CR-20261009-001 L3.1), on an RM-hosted meeting only (canvas/privacy.js): the builder's person on the
  // site camera hears voices and nothing else. Her status goes to everyone but the site phone; her requests — the cost
  // sheet, a booking, a signal — to the host and the RM alone; an empty list sends nothing, never a broadcast. A meeting
  // a sales manager hosts (no RM stamp in the link answer) sends exactly as today.
  const privacy = customerPrivacy({ send, room, meeting: info.meeting });
  const toStaff = privacy.toStaff;
  const scenes = canvas.scenes;
  const st = {
    lang, follow: true, face: false, share: false,
    host: { scene: scenes[0].id, variant: null, spot: null, zoom: 1, room: null, yaw: 40 },
    own: null, ptr: null, key: "", sheet: null, menu: false, card: null, nudge: true,
    booking: info.meeting.booking || null, lastUnit: null, dwell: {}, sentDwell: "", hostHere: false,
    pick: null, pickup: true, penTool: "pen",
  };
  // Read when she looks, not once at load: rejoining the next day never offers a time that has gone.
  const slots = () => openSlots(info.meeting.startsAt);
  const vars = () => ({ host: hostFirst });
  const view = () => (st.follow ? st.host : st.own);
  // His ink, as he last sent it (the navigator's book is the truth), and the strokes he is drawing right now.
  const book = new InkBook();
  const live = new Map();
  canvas.stills = canvas.stills || new Map(); // stills he froze from a shared tab (W3): n → { src, w, h }
  // Who holds a pen (the host's latest word), her own stroke and pointer, and everyone else's pointers (W2).
  let holders = new Set();
  let mine = null, pointing = null;
  const peers = new Map();
  const me = () => room.localParticipant.identity;
  const holdsPen = () => holders.has(me());
  const nameOf = (identity) => firstName(room.remoteParticipants.get(identity)?.name) || "Someone";

  // ------------------------------------------------------------------------------------------ the frame --
  const titleK = h("div", { class: "k" });
  const titleT = h("div", { class: "t" });
  const titleN = h("div", { class: "n" });
  const dots = h("div", { class: "dots" });
  const cvLayer = h("div", { class: "cv-layer" });
  // Opening the location in Google Maps is worth the sales manager knowing.
  cvLayer.addEventListener("click", (e) => {
    const a = e.target.closest("[data-map]");
    if (a) signal(a.dataset.map === "directions" ? "Asked Google Maps for directions to the site" : "Opened the location in Google Maps", "map");
  });
  const faceVideo = h("video", { autoplay: true, playsinline: true, muted: true });
  const faceLayer = h("div", { class: "bigvid", hidden: true }, faceVideo,
    h("div", { class: "bv-face", text: initials(hostName) }), h("div", { class: "bv-name" }, h("b", { text: hostName }), words.hostLabel));
  // His shared tab, at the tab's own size, with the same ink layer and pointer as a page (W3): drawn on, pointed at and
  // zoomed like a plan. The marks are on the glass; a mark worth keeping he freezes into a still.
  const shareVideo = h("video", { autoplay: true, playsinline: true, muted: true });
  const shareFit = h("div", { class: "cv-fit", "data-w": "1600", "data-h": "1000" }, shareVideo);
  shareFit.insertAdjacentHTML("beforeend", '<svg class="ink" viewBox="0 0 1600 1000" preserveAspectRatio="none" aria-hidden="true"></svg><div class="ink-lbls"></div><div class="ptr" hidden><span></span></div>');
  shareFit.querySelector(".ptr span").textContent = hostFirst;
  const shareLayer = h("div", { class: "share-layer", hidden: true }, h("div", { class: "cv share" }, h("div", { class: "cv-center" }, shareFit)), h("div", { class: "share-note" }));
  const shareSized = () => {
    if (!shareVideo.videoWidth || !shareVideo.videoHeight) return;
    shareFit.dataset.w = shareVideo.videoWidth;
    shareFit.dataset.h = shareVideo.videoHeight;
    shareFit.querySelector(".ink").setAttribute("viewBox", `0 0 ${shareVideo.videoWidth} ${shareVideo.videoHeight}`);
    fitPlans(shareLayer);
    applyShareZoom();
    paintCustInk();
  };
  shareVideo.addEventListener("loadedmetadata", shareSized);
  shareVideo.addEventListener("resize", shareSized);
  // His zoom on the tab, or hers while she pinches to read something small.
  function applyShareZoom() {
    const o = st.shareOwn;
    shareFit.style.transformOrigin = "50% 50%";
    shareFit.style.transform = o ? `translate(${o.tx || 0}px, ${o.ty || 0}px) scale(${o.zoom})` : `scale(${st.host.sz || 1})`;
  }
  const waitCard = h("div", { class: "waiting", hidden: true });
  const followBtn = h("button", { class: "follow", type: "button", onclick: () => (st.follow ? explore("chip") : followAgain()) });
  const pipVideo = h("video", { autoplay: true, playsinline: true, muted: true });
  const pip = h("button", { class: "pip", type: "button", "aria-label": `See ${hostFirst} full screen`, onclick: () => setFace(true) },
    pipVideo, h("div", { class: "face", text: initials(hostName) }), h("span", { text: `${hostFirst} ⤢` }));
  const thumb = h("button", { class: "tour-thumb", type: "button", hidden: true, "aria-label": "Back to the tour", onclick: () => setFace(false) });
  const selfVideo = h("video", { autoplay: true, playsinline: true, muted: true });
  const selfie = h("div", { class: "selfie", hidden: true }, selfVideo);
  const zoomers = h("div", { class: "zoomers", hidden: true },
    h("button", { type: "button", "aria-label": "Zoom in", text: "+", onclick: () => zoomBy(0.5) }),
    h("button", { type: "button", "aria-label": "Zoom out", text: "−", onclick: () => zoomBy(-0.5) }));
  const arrows = h("div", { class: "nav-arrows", hidden: true },
    h("button", { type: "button", "aria-label": "Previous", text: "‹", onclick: () => step(-1) }),
    h("button", { type: "button", "aria-label": "Next", text: "›", onclick: () => step(1) }));
  const inkChip = h("div", { class: "ink-chip", hidden: true });
  const penBar = h("div", { class: "cx-pen", hidden: true, role: "toolbar", "aria-label": "Your pen" });
  const stage = h("div", { class: "c-stage" }, cvLayer, shareLayer, faceLayer, waitCard, followBtn, pip, thumb, selfie, zoomers, arrows, inkChip, penBar);

  // Only her own link books the visit (book.js); her family and the RM follow the tour without the button.
  const bookBtn = h("button", { class: "cta primary", type: "button", hidden: info.you.role !== "customer", onclick: () => openSheet("book") });
  const costBtn = h("button", { class: "cta", type: "button", onclick: () => openSheet("cost") });
  const askBtn = h("button", { class: "cta", type: "button", onclick: () => openSheet("ask") });
  const micBtn = h("button", { class: "mini", type: "button", onclick: () => toggleMic() });
  const camBtn = h("button", { class: "mini", type: "button", onclick: () => toggleCam() });
  const moreBtn = h("button", { class: "mini", type: "button", "aria-label": "More", onclick: () => { st.menu = !st.menu; paintOverlays(); } });
  moreBtn.innerHTML = icon("more");
  const leaveBtn = h("button", { class: "mini leave", type: "button", onclick: () => session.leave() });
  const dock = h("footer", { class: "dock" }, h("div", { class: "ctas" }, bookBtn, costBtn, askBtn), h("div", { class: "minis" }, micBtn, camBtn, moreBtn, leaveBtn));
  const overlays = h("div", { class: "overlays" });
  const shell = h("div", { class: "cx" }, h("header", { class: "c-top" }, h("div", { class: "ttl" }, titleK, titleT), titleN), dots, stage, dock, overlays);
  document.body.classList.add("ms-dark");
  root.replaceChildren(shell);

  // ------------------------------------------------------------------------------------------ painting --
  function unitFor(v) {
    const sc = sceneOf(canvas, v.scene);
    const variant = sc.kind === "plan" ? variantOf(sc, v) : null;
    if (variant) st.lastUnit = variant.name;
    return variant ? variant.name : st.lastUnit || canvas.project;
  }
  function ctx() {
    return { who: "cust", lang: st.lang, first: firstName(info.you.name), hostFirst, booking: st.booking, slots: slots(), unit: unitFor(view()), lookAround: t("lookAround", st.lang) };
  }
  // Attention is measured, not sampled: the time between changes of what is on her screen. Only while the sales
  // manager is there and she is looking at the tour (not at his video).
  let cur = null;
  function account() {
    const now = performance.now();
    if (cur && cur.counting) st.dwell[cur.scene] = (st.dwell[cur.scene] || 0) + (now - cur.since) / 1000;
    cur = { scene: view().scene, since: now, counting: !st.face && st.hostHere };
  }
  function paint(force = false) {
    account();
    const v = view();
    const sc = sceneOf(canvas, v.scene);
    const key = [v.scene, sc.kind === "plan" ? v.variant : "", roomOf(sc, v)?.id || "", st.lang, st.booking ? st.booking.at : ""].join("|");
    // What plays stays put while its scene does — following or exploring, full screen and back — so no panorama
    // reloads and the film keeps its place (audit F9, F10).
    const keep = ["pano", "video", "embed"].includes(sc.kind) && key === st.key && cvLayer.querySelector(".pano360, .cv-video, .embed-frame");
    if ((force || key !== st.key) && !keep) {
      panoIn(cvLayer)?.destroy();
      cvLayer.innerHTML = canvasHTML(canvas, v, ctx());
      st.key = key;
      fitPlans(cvLayer);
      const box = cvLayer.querySelector(".pano360");
      if (box) mountPano(box, sc.pano, canvas.base, { who: "cust", hostFirst, onLook: lookAround, onGo: stepTo, onHold: (uv, frame) => onPanoHold(uv, frame) });
    }
    applyView(cvLayer, canvas, v, st.follow ? null : st.own);
    film();
    paintPointer(cvLayer, st.follow && !st.face && !st.share ? st.ptr : null);
    paintPointer(shareLayer, st.share && !st.face ? st.ptr : null);
    paintCustInk();
    const idx = scenes.findIndex((s) => s.id === sc.id);
    titleK.textContent = `${canvas.project} · ${hostFirst}`;
    titleT.textContent = pickLang(sc.name, st.lang);
    titleN.textContent = idx < 0 ? (sc.kind === "board" ? "Board" : "Still") : `${idx + 1} / ${scenes.length}`;
    dots.replaceChildren(...scenes.map((s, i) => h("i", { class: i < idx ? "done" : i === idx ? "now" : "" })));
    followBtn.hidden = st.face || st.share;
    followBtn.classList.toggle("off", !st.follow);
    followBtn.replaceChildren(h("i"), t(st.follow ? "following" : "exploring", st.lang, vars()));
    const plan = ["plan", "fit", "board", "still"].includes(sc.kind);
    zoomers.hidden = st.follow || st.face || !plan;
    arrows.hidden = st.follow || st.face;
    faceLayer.hidden = !st.face;
    pip.hidden = st.face;
    thumb.hidden = !st.face;
    if (st.face) thumb.replaceChildren(h("span", { style: `background-image:url('${thumbOf(canvas, sc, v)}')` }), h("em", { text: `${pickLang(sc.name, st.lang)} ⤡` }));
    shareLayer.hidden = !st.share || st.face;
    shareLayer.querySelector(".share-note").textContent = t(st.siteOn ? "siteCam" : "sharing", st.lang, vars());
    waitCard.hidden = st.hostHere;
    if (!st.hostHere) waitCard.replaceChildren(h("div", { class: "card" }, h("h2", {}, h("span", { class: "pulse" }), t("waiting", st.lang, vars()))));
    bookBtn.textContent = st.booking ? `✓ ${t("booked", st.lang)}` : t("book", st.lang);
    costBtn.textContent = t("cost", st.lang);
    askBtn.textContent = t("ask", st.lang);
    leaveBtn.textContent = t("leave", st.lang);
    const lp = room.localParticipant;
    micBtn.innerHTML = icon(lp.isMicrophoneEnabled ? "mic" : "micOff");
    micBtn.classList.toggle("off", !lp.isMicrophoneEnabled);
    micBtn.setAttribute("aria-label", lp.isMicrophoneEnabled ? "Mute" : "Unmute");
    camBtn.innerHTML = icon(lp.isCameraEnabled ? "video" : "videoOff");
    camBtn.classList.toggle("off", !lp.isCameraEnabled);
    camBtn.setAttribute("aria-label", lp.isCameraEnabled ? "Turn camera off" : "Turn camera on");
  }

  // The host's picture: in the small window, full screen when she asks, and his shared window when he shares one.
  function hostParticipant() {
    return [...room.remoteParticipants.values()].find((p) => roleOf(p) === "host") || null;
  }
  let attached = { cam: null, share: null, self: null };
  function paintVideo() {
    const host = hostParticipant();
    st.hostHere = Boolean(host);
    const cam = host?.getTrackPublication(Track.Source.Camera);
    const camTrack = cam && !cam.isMuted ? cam.track : null;
    if (camTrack !== attached.cam) {
      if (attached.cam) { attached.cam.detach(pipVideo); attached.cam.detach(faceVideo); }
      if (camTrack) { camTrack.attach(pipVideo); camTrack.attach(faceVideo); }
      attached.cam = camTrack;
    }
    pip.classList.toggle("has-video", Boolean(camTrack));
    faceLayer.classList.toggle("has-video", Boolean(camTrack));
    const sh = host?.getTrackPublication(Track.Source.ScreenShare);
    // Or the site camera he has put on the stage (stage 11): that participant's camera, and only ever a site camera's —
    // the identity in his state is checked for its role, so it can never bring up a guest's or her own face.
    // And only while he is here: if he leaves, the stage he chose goes with him (audit S1, P2).
    const siteP = host && st.host.cam && roleOf({ identity: st.host.cam }) === "site" ? room.remoteParticipants.get(st.host.cam) : null;
    const sitePub = siteP?.getTrackPublication(Track.Source.Camera);
    const tabTrack = sh && !sh.isMuted ? sh.track || null : null;
    const shTrack = tabTrack || (sitePub && !sitePub.isMuted ? sitePub.track || null : null);
    st.siteOn = Boolean(shTrack && !tabTrack);
    if (shTrack !== attached.share) {
      attached.share?.detach(shareVideo);
      if (shTrack) shTrack.attach(shareVideo);
      attached.share = shTrack;
    }
    if (st.share && !shTrack) st.shareOwn = null;
    st.share = Boolean(shTrack);
    const mine = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const selfTrack = mine && !mine.isMuted ? mine.track : null;
    if (selfTrack !== attached.self) {
      attached.self?.detach(selfVideo);
      if (selfTrack) selfTrack.attach(selfVideo);
      attached.self = selfTrack;
    }
    selfie.hidden = !selfTrack;
    const talking = host && host.isSpeaking;
    pip.classList.toggle("talk", Boolean(talking));
    faceLayer.classList.toggle("talk", Boolean(talking));
    paint();
  }

  function paintOverlays() {
    const parts = [];
    if (st.nudge) {
      parts.push(h("div", { class: "nudge" }, h("b", { text: "Would you like to turn your camera on?" }),
        `${hostFirst} would love to see you — or stay as you are; the tour works either way.`,
        h("div", { class: "row" },
          h("button", { class: "btn-s", type: "button", text: "Turn on camera", onclick: () => { st.nudge = false; toggleCam(true); paintOverlays(); } }),
          h("button", { class: "btn-s ghost", type: "button", text: "Not now", onclick: () => { st.nudge = false; paintOverlays(); } }))));
    }
    if (st.card) parts.push(st.card);
    if (st.menu) {
      parts.push(h("div", { class: "menu-s", role: "menu" },
        h("div", { class: "langs" }, LANGS.map(([k, n]) => h("button", { type: "button", class: st.lang === k ? "on" : "", text: n, onclick: () => { st.lang = k; st.menu = false; paint(true); paintOverlays(); } }))),
        h("button", { type: "button", text: "Close", onclick: () => { st.menu = false; paintOverlays(); } })));
    }
    if (st.sheet) parts.push(h("div", { class: "scrim", onclick: () => { st.sheet = null; paintOverlays(); } }), sheet());
    overlays.replaceChildren(...parts);
  }

  function sheet() {
    const close = h("button", { class: "btn-s ghost", type: "button", text: "Not now", onclick: () => { st.sheet = null; paintOverlays(); } });
    if (st.sheet === "book") {
      const open = slots();
      if (st.pick && !open.some((s) => s.at === st.pick.at)) st.pick = null;
      const go = h("button", { class: "btn-s", type: "button", text: "Book it", disabled: st.pick == null, onclick: () => doBook(go) });
      return h("div", { class: "sheet", role: "dialog", "aria-label": "Book your site visit" },
        h("h5", { text: "Book your site visit" }), h("p", { text: `Pick a time. ${hostFirst} meets you at ${canvas.project}; a car can collect you.` }),
        h("div", { class: "chips" }, open.map((s) => h("button", { class: "chip" + (st.pick?.at === s.at ? " on" : ""), type: "button", text: `${s.day} · ${s.time}`, onclick: () => { st.pick = s; paintOverlays(); } }))),
        h("div", { class: "chips" }, h("button", { class: "chip" + (st.pickup ? " on" : ""), type: "button", text: "🚗 Pick me up from home", onclick: () => { st.pickup = !st.pickup; paintOverlays(); } })),
        h("div", { class: "row" }, close, go));
    }
    if (st.sheet === "cost") {
      const unit = unitFor(view());
      return h("div", { class: "sheet", role: "dialog", "aria-label": "The cost sheet" },
        h("h5", { text: "The cost sheet on WhatsApp" }),
        h("p", {}, "For the ", h("b", { text: unit }), words.priceLine(hostFirst)),
        h("div", { class: "row" }, close, h("button", { class: "btn-s", type: "button", text: `Ask ${hostFirst} to send it`, onclick: () => request({ kind: "cost", unit }) })));
    }
    const input = h("input", { maxlength: "200", placeholder: "Or type your question", "aria-label": "Your question" });
    return h("div", { class: "sheet", role: "dialog", "aria-label": `Ask ${hostFirst}` },
      h("h5", { text: `Ask ${hostFirst}` }), h("p", { text: "He sees it at once — no need to interrupt." }),
      h("div", { class: "chips" }, QUESTIONS.map((q) => h("button", { class: "chip", type: "button", text: q, onclick: () => request({ kind: "ask", text: q }) }))),
      input,
      h("div", { class: "row" }, close, h("button", { class: "btn-s", type: "button", text: "Send", onclick: () => { const q = input.value.trim(); if (q) request({ kind: "ask", text: q }); } })));
  }
  function openSheet(which) {
    if (which === "book" && st.booking) { toast(`Booked — ${st.booking.day}, ${st.booking.time}.`); return; }
    st.sheet = which; st.pick = null; st.menu = false; paintOverlays();
  }

  // ------------------------------------------------------------------------------------------ actions --
  async function toggleMic() {
    try { await room.localParticipant.setMicrophoneEnabled(!room.localParticipant.isMicrophoneEnabled); } catch { toast("Your microphone is blocked — allow it in the browser's settings."); }
    paintVideo();
  }
  async function toggleCam(on) {
    const want = on ?? !room.localParticipant.isCameraEnabled;
    try { await room.localParticipant.setCameraEnabled(want); } catch { toast("Your camera is blocked — allow it in the browser's settings."); }
    st.nudge = false;
    paintVideo();
    paintOverlays();
  }
  function status() {
    const v = view();
    privacy.status(TOPIC.STATUS, { follow: st.follow, face: st.face, scene: v.scene, variant: v.variant || null, room: v.room || null });
  }
  function signal(text, t2 = "zoom", extra = {}) {
    toStaff(TOPIC.REQ, { id: crypto.randomUUID(), kind: "sig", text });
    postEvents(code, [{ t: t2, text, ...extra }]);
  }
  function explore(why) {
    if (!st.follow) return;
    st.follow = false;
    st.own = { ...st.host, spot: null, zoom: 1, tx: 0, ty: 0 };
    status();
    signal(why === "chip" ? "Started exploring on her own" : "Started looking around on her own", "explore");
    paint(true);
  }
  // The film plays in step with the sales manager's: where his is now is where it was when he said so, plus the time
  // since. Off by more than a moment, hers jumps there. A phone that will not start sound starts it silent and offers
  // one tap for sound. Exploring, she has the controls herself.
  function film() {
    const el = cvLayer.querySelector(".cv-video");
    if (!el) return;
    const sound = cvLayer.querySelector(".vid-sound");
    if (!sound.dataset.wired) {
      sound.dataset.wired = "1";
      sound.addEventListener("click", () => { el.muted = false; sound.hidden = true; el.play().catch(() => {}); });
    }
    el.controls = !st.follow;
    if (!st.follow) return;
    const vid = st.host.vid || { on: false, t: 0 };
    const at = vid.on ? vid.t + (performance.now() - (st.vidAt || performance.now())) / 1000 : vid.t;
    if (Number.isFinite(at) && Math.abs((el.currentTime || 0) - at) > 0.8) { try { el.currentTime = at; } catch { /* not loaded yet */ } }
    if (vid.on && el.paused) {
      el.play().catch(() => { el.muted = true; sound.hidden = false; el.play().catch(() => {}); });
    } else if (!vid.on && !el.paused) el.pause();
  }
  // In the 360° walk-through she can look around and step elsewhere on her own; either one means she is exploring.
  function lookAround(look, ended) {
    if (st.follow) explore("gesture");
    st.own = { ...st.own, room: look.frame, yaw: look.yaw, pitch: look.pitch };
    if (ended) status();
  }
  function stepTo(to) {
    if (st.follow) explore("gesture");
    st.own = { ...st.own, room: to };
    paint();
    status();
  }
  function followAgain() {
    st.follow = true;
    st.card = null;
    status();
    paint(true);
    paintOverlays();
  }
  function setFace(on) {
    st.face = on;
    st.card = null;
    status();
    if (on) postEvents(code, [{ t: "face" }]);
    paint(true);
    paintOverlays();
  }
  function step(d) {
    const i = scenes.findIndex((s) => s.id === st.own.scene) + d;
    if (!scenes[i]) return;
    st.own = { scene: scenes[i].id, variant: st.own.variant, room: st.own.room, yaw: 40, spot: null, zoom: 1, tx: 0, ty: 0 };
    status();
    paint(true);
  }
  function zoomBy(dz) {
    st.own.zoom = Math.max(1, Math.min(4, (st.own.zoom || 1) + dz));
    if (st.own.zoom === 1) { st.own.tx = 0; st.own.ty = 0; }
    paint();
    zoomSignal();
  }
  // What she zoomed into: the hotspot nearest the middle of her screen.
  function zoomSignal() {
    if ((st.own.zoom || 1) < 1.6) return;
    const f = cvLayer.querySelector(".cv-fit");
    if (!f) return;
    const r = f.getBoundingClientRect(), s = cvLayer.getBoundingClientRect();
    const cx = ((s.left + s.width / 2 - r.left) / r.width) * 100, cy = ((s.top + s.height / 2 - r.top) / r.height) * 100;
    const spots = spotsOf(sceneOf(canvas, st.own.scene), st.own);
    let best = null, bd = Infinity;
    for (const p of spots) { const d = (p.x - cx) ** 2 + (p.y - cy) ** 2; if (d < bd) { bd = d; best = p; } }
    if (best && bd < 900) signal(`Looked closely: ${best.label}`, "zoom", { scene: st.own.scene });
  }
  async function doBook(btn) {
    const s = st.pick;
    btn.disabled = true;
    try {
      const res = await bookVisit(code, s.at, st.pickup);
      st.booking = res.booking;
      toStaff(TOPIC.REQ, { id: crypto.randomUUID(), kind: "book", day: s.day, time: s.time, pickup: st.pickup });
      st.sheet = null;
      toast(`Booked — ${s.day}, ${s.time}. ${hostFirst} will confirm on WhatsApp.`);
      paint(true);
      paintOverlays();
    } catch (err) {
      // The sheet is drawn again from the times still open, so a time that has just gone disappears from it.
      st.pick = null;
      paintOverlays();
      toast(err.message);
    }
  }
  function request(r) {
    const body = { id: crypto.randomUUID(), ...r };
    toStaff(TOPIC.REQ, body);
    postEvents(code, [r.kind === "ask" ? { t: "ask", text: r.text } : { t: "request", kind: r.kind, unit: r.unit }]);
    st.sheet = null;
    toast(r.kind === "ask" ? `${hostFirst} has your question` : `${hostFirst} will send it on WhatsApp`);
    paintOverlays();
  }
  function askCard(title, line, yes, yesText, noText) {
    st.card = h("div", { class: "nudge" }, h("b", { text: title }), line,
      h("div", { class: "row" }, h("button", { class: "btn-s", type: "button", text: yesText, onclick: yes }),
        h("button", { class: "btn-s ghost", type: "button", text: noText, onclick: () => { st.card = null; paintOverlays(); } })));
    paintOverlays();
  }

  // ------------------------------------------------------------------------------------------ ink --
  // The page she is looking at shows its ink — his, wherever she is in the story, whether following or exploring.
  const inkRoot = () => (st.share ? shareLayer : cvLayer);
  function paintCustInk() {
    const page = curPage();
    const drawing = page ? [...live.values()].filter((l) => l.page === page) : [];
    if (page) paintInk(inkRoot(), book.get(page), [...drawing, ...(mine && mine.page === page ? [mine] : [])]);
    // Pointers: on a page, in the 360° (a direction), or on the price page (a place in a row) — hers shown as "You".
    const v = view(), sc = sceneOf(canvas, v.scene);
    const ptrPage = st.share ? "share" : pointerPage(sc, v);
    const dots = [...peers.values()].filter((p) => p.page === ptrPage).map((p) => ({ ...p, colour: colourOf(p.id) }));
    if (pointing && pointing.page === ptrPage) dots.push({ ...pointing, id: me(), name: "You", colour: colourOf(me()) });
    const pv = !st.share && panoIn(cvLayer);
    if (pv) pv.peers(dots);
    else if (!st.share && sc.kind === "price") paintDocPeers(cvLayer, dots, docSpot);
    else if (ptrPage) paintPeers(inkRoot(), dots);
    const who = drawing.find((l) => !l.fading);
    inkChip.hidden = !page || st.face || !who;
    if (who) inkChip.textContent = `✎ ${roleOf({ identity: who.by }) === "host" ? hostFirst : nameOf(who.by)} is drawing`;
    paintPenBar();
  }
  function paintPenBar() {
    const has = holdsPen() && !st.face;
    penBar.hidden = !has;
    stage.classList.toggle("has-pen", has);
    if (!has) return;
    const b = (k, label) => h("button", { type: "button", class: st.penTool === k ? "on" : "", text: label, "aria-pressed": String(st.penTool === k), onclick: () => { st.penTool = k; paintPenBar(); } });
    penBar.replaceChildren(h("span", { class: "me", style: `background:${colourOf(me())}`, title: "Your colour" }), b("point", "● Point"), b("pen", "✎ Pen"), b("label", "T Note"),
      h("button", { type: "button", text: "↶", "aria-label": "Undo my last mark", onclick: () => undoMine() }),
      h("button", { type: "button", class: "done", text: "Done", onclick: () => toStaff(TOPIC.REQ, { id: crypto.randomUUID(), kind: "pen-done" }) }));
  }
  // The host's word on who holds a pen. Gaining one says how it works; losing one ends any stroke in progress.
  function onPen(body) {
    const had = holdsPen();
    holders = new Set((Array.isArray(body?.holders) ? body.holders : []).filter((i) => typeof i === "string").slice(0, 12));
    for (const [id, l] of live) if (roleOf({ identity: l.by }) !== "host" && !holders.has(l.by)) live.delete(id);
    if (!had && holdsPen()) {
      st.penTool = "pen";
      askCard(`${hostFirst} gave you a pen`, "Draw on the plan with one finger. Two fingers still move and zoom it, and pressing and holding points.", () => { st.card = null; paintOverlays(); }, "Start drawing", "Later");
    }
    if (had && !holdsPen()) { cancelMine(); toast(`The pen is back with ${hostFirst}`); }
    paintCustInk();
  }
  // Her own marks, in her colour, sent to everyone; the host keeps them in his book with his own.
  const pushMine = throttle(() => {
    if (!mine) return;
    const pts = mine.pts.slice(mine.sent);
    if (!pts.length) return;
    mine.sent = mine.pts.length;
    send(TOPIC.INK_LIVE, { id: mine.id, page: mine.page, tool: "pen", pts }, { reliable: false });
  }, 60);
  function curPage() { if (st.share) return "share"; const v = view(); return pageKey(sceneOf(canvas, v.scene), v); }
  function startMine(e) {
    const page = curPage();
    const pt = page && unitsAt(inkRoot().querySelector(".cv-fit"), e.clientX, e.clientY);
    if (!pt) return false;
    if (st.penTool === "label") { askLabel(page, pt); return true; }
    mine = { id: inkId(), page, tool: "pen", pts: [pt], sent: 0, pointerId: e.pointerId, by: me() };
    pushMine();
    paintCustInk();
    return true;
  }
  function moveMine(e) {
    const pt = unitsAt(inkRoot().querySelector(".cv-fit"), e.clientX, e.clientY, true);
    const q = mine.pts[mine.pts.length - 1];
    if (!pt || Math.hypot(pt[0] - q[0], pt[1] - q[1]) < 35 || mine.pts.length >= LIMITS.points) return;
    mine.pts.push(pt);
    pushMine();
    paintCustInk();
  }
  function endMine() {
    const m = mine;
    mine = null;
    if (!m || !holdsPen()) { paintCustInk(); return; }
    if (m.pts.length === 1) m.pts.push([m.pts[0][0] + 10, m.pts[0][1]]);
    const s = book.add(m.page, { id: m.id, tool: "pen", pts: m.pts, by: me() });
    if (s) sendText(TOPIC.INK, { page: m.page, stroke: s });
    paintCustInk();
  }
  function cancelMine() {
    if (!mine) return;
    send(TOPIC.INK_LIVE, { id: mine.id, page: mine.page, cancel: true }, { reliable: false });
    mine = null;
    paintCustInk();
  }
  function undoMine() {
    const page = curPage();
    const last = page && book.lastBy(page, me());
    if (!last) return;
    book.remove(page, [last.id]);
    send(TOPIC.INK_OP, { op: "del", page, ids: [last.id] });
    paintCustInk();
  }
  function askLabel(page, pt) {
    const f = inkRoot().querySelector(".cv-fit");
    if (!f) return;
    const input = h("input", { class: "ink-in", maxlength: String(LIMITS.label), placeholder: "Your note", "aria-label": "A note on the plan", enterkeyhint: "done", style: `left:${pt[0] / 100}%;top:${pt[1] / 100}%` });
    f.append(input);
    input.focus();
    let done = false;
    const finish = (keep) => {
      if (done) return;
      done = true;
      const text = input.value.trim();
      input.remove();
      if (!keep || !text || !holdsPen()) return;
      const s = book.add(page, { id: inkId(), tool: "label", pts: [pt], text, by: me() });
      if (s) sendText(TOPIC.INK, { page, stroke: s });
      paintCustInk();
    };
    input.addEventListener("keydown", (e) => { if (e.key === "Enter") finish(true); if (e.key === "Escape") finish(false); });
    input.addEventListener("blur", () => finish(true));
    input.addEventListener("pointerdown", (e) => e.stopPropagation());
  }
  // Pressing and holding points: a dot on every screen, named, while her finger is down.
  const pushPoint = throttle((b) => { if (pointing) send(TOPIC.PEER_PTR, b, { reliable: false }); }, 50);
  function pointAt(e) {
    const sc = sceneOf(canvas, view().scene);
    if (!st.share && sc.kind === "price") {
      const a = docAnchor(cvLayer.querySelector(".cv-doc"), e.clientX, e.clientY);
      return a ? { page: sc.id, ...a } : null;
    }
    const page = curPage();
    const pt = page && unitsAt(inkRoot().querySelector(".cv-fit"), e.clientX, e.clientY, true);
    return pt ? { page, x: pt[0] / 100, y: pt[1] / 100 } : null;
  }
  function startPointing(e) {
    const b = pointAt(e);
    if (!b) return false;
    pointing = { pointerId: e.pointerId, ...b };
    send(TOPIC.PEER_PTR, b, { reliable: false });
    paintCustInk();
    return true;
  }
  function movePointing(e) {
    const b = pointAt(e);
    if (!b) return;
    Object.assign(pointing, b);
    pushPoint(b);
    paintCustInk();
  }
  function stopPointing() {
    if (!pointing) return;
    pointing = null;
    send(TOPIC.PEER_PTR, null, { reliable: false });
    setTimeout(() => { if (!pointing) send(TOPIC.PEER_PTR, null, { reliable: false }); }, 120);
    paintCustInk();
  }
  function onPanoHold(uv, frame) {
    if (!uv) { stopPointing(); return; }
    const b = { page: sceneOf(canvas, view().scene).id, frame, u: uv.u, v: uv.v };
    if (!pointing) { pointing = { pointerId: "pano", ...b }; send(TOPIC.PEER_PTR, b, { reliable: false }); }
    else { Object.assign(pointing, b); pushPoint(b); }
    paintCustInk();
  }
  function onPeerPointer(body, p) {
    const was = peers.get(p.identity);
    const next = peerFrom(body, p, firstName(p.name) || "Someone");
    if (next) {
      peers.set(p.identity, next);
    } else if (was) {
      was.gone = true;
      setTimeout(() => { if (peers.get(p.identity) === was && was.gone) { peers.delete(p.identity); paintCustInk(); } }, 1400);
    }
    paintCustInk();
  }
  let tidy = 0;
  // A stroke whose sender went quiet (a dropped connection mid-stroke) is let go after a few seconds.
  function tidyLater() {
    clearTimeout(tidy);
    tidy = setTimeout(() => {
      const now = Date.now();
      for (const [id, l] of live) if (!l.fading && now - l.at > 4000) live.delete(id);
      paintCustInk();
      if (live.size) tidyLater();
    }, 4200);
  }
  function onInkLive(body, sender) {
    if (typeof body.id !== "string" || !pageOk(canvas, body.page)) return;
    const prev = live.get(body.id);
    if (prev && prev.by !== sender) return;
    if (body.cancel) { live.delete(body.id); paintCustInk(); return; }
    // Strokes in progress are few; a flood of new ones from a pen holder is not kept (audit, P1).
    if (!prev && live.size >= 24 && roleOf({ identity: sender }) !== "host") return;
    const pts = body.tool === "arrow" ? body.pts : [...(prev ? prev.pts : []), ...(Array.isArray(body.pts) ? body.pts : [])];
    const s = cleanStroke({ id: body.id, tool: body.tool, pts: Array.isArray(pts) ? pts.slice(0, 2000) : [], by: sender });
    if (!s || s.tool === "label") return;
    if (roleOf({ identity: sender }) !== "host" && !["pen", "hl"].includes(s.tool)) return;
    const entry = { ...s, page: body.page, at: Date.now(), fading: Boolean(prev?.fading) };
    live.set(body.id, entry);
    if (body.end) {
      entry.fading = true;
      setTimeout(() => { live.delete(body.id); paintCustInk(); }, 1700);
    }
    tidyLater();
    paintCustInk();
  }
  // A delete takes what its sender may take (the host anything, a pen holder her own); only the host clears a page.
  function onInkOp(body, sender) {
    if (!pageOk(canvas, body.page)) return;
    const isHost = roleOf({ identity: sender }) === "host";
    if (body.op === "del") book.remove(body.page, allowedDeletes(book.get(body.page), body.ids, sender, isHost));
    if (body.op === "clear" && isHost) book.clear(body.page);
    paintCustInk();
  }
  // Finished strokes arrive as text streams — from the host or someone holding a pen; a whole book from the host alone.
  room.registerTextStreamHandler(TOPIC.INK, async (reader, from) => {
    const who = { identity: from?.identity };
    if (!mayDraw(who, holders)) return;
    const isHost = roleOf(who) === "host";
    // A pen holder sends one small stroke at a time; a larger stream from anyone but the host is not read (audit, P1).
    if (!isHost && (reader.info?.size || 0) > LIMITS.stream) return;
    let text;
    try { text = isHost ? await reader.readAll() : await readCapped(reader, LIMITS.stream); } catch { return; }
    if (text == null) return;
    let body;
    try { body = JSON.parse(text); } catch { return; }
    if (!body || typeof body !== "object" || !mayDraw(who, holders)) return;
    if (body.snapshot) { if (isHost) book.load(body.snapshot); }
    else if (body.owner && body.book) { if (isHost && typeof body.owner === "string") book.syncOwner(body.owner, body.book); }
    else if (pageOk(canvas, body.page)) {
      if (body.stroke) {
        book.add(body.page, { ...body.stroke, by: from.identity });
        if (live.get(body.stroke.id)?.by === from.identity) live.delete(body.stroke.id);
      }
      if (Array.isArray(body.strokes) && isHost) for (const s of body.strokes.slice(0, 300)) book.add(body.page, s);
    }
    paintCustInk();
  });

  // A still he froze from his tab arrives as a file — from the host alone — and becomes a page every screen can show.
  room.registerByteStreamHandler(TOPIC.STILL, async (reader, from) => {
    if (roleOf({ identity: from?.identity }) !== "host") return;
    const m = /^still-([1-9]\d?)-(\d{2,5})x(\d{2,5})\.jpg$/.exec(reader.info?.name || "");
    if (!m || +m[1] > LIMITS.stills || (reader.info?.size || 0) > 4_000_000) return;
    let chunks;
    try { chunks = await reader.readAll(); } catch { return; }
    const n = +m[1];
    const old = canvas.stills.get(n);
    if (old?.src) URL.revokeObjectURL(old.src);
    canvas.stills.set(n, { src: URL.createObjectURL(new Blob(chunks, { type: "image/jpeg" })), w: +m[2], h: +m[3] });
    if (view().scene === `still:${n}`) paint(true);
  });

  // ------------------------------------------------------------------------------------------ gestures --
  // Pinch and drag on a plan, drag in the walk-through. A tap is not a gesture: it never breaks following. With a pen
  // (W2) one finger draws, and a second finger lets the stroke go and pinches; pressing and holding points, anyone.
  const pointers = new Map();
  let g = null;
  let holdT = 0;
  let priceHold = null;
  stage.addEventListener("pointerdown", (e) => {
    if (st.face || e.target.closest("button, a, input, .sheet, .nudge, .cx-pen")) return;
    const kind = st.share ? "share" : sceneOf(canvas, view().scene).kind;
    // The price page scrolls natively: only a press and hold there points (a place in a row), nothing else is taken.
    if (kind === "price") {
      const at = { clientX: e.clientX, clientY: e.clientY, pointerId: e.pointerId };
      priceHold = { id: e.pointerId, x: e.clientX, y: e.clientY };
      clearTimeout(holdT);
      holdT = setTimeout(() => { if (priceHold && priceHold.id === at.pointerId) startPointing(at); }, 350);
      return;
    }
    if (!["plan", "fit", "tour", "board", "still", "share"].includes(kind)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    stage.setPointerCapture?.(e.pointerId);
    clearTimeout(holdT);
    const flat = kind !== "tour";
    if (pointers.size > 1) { cancelMine(); stopPointing(); }
    // Taking the default away keeps a note's input focused (a mouse-down would move focus straight off it).
    else if (flat && holdsPen() && st.penTool !== "point") { if (startMine(e)) { e.preventDefault(); g = null; return; } }
    else if (flat && holdsPen() && st.penTool === "point") { if (startPointing(e)) { e.preventDefault(); g = null; return; } }
    else if (flat) {
      const at = { clientX: e.clientX, clientY: e.clientY, pointerId: e.pointerId };
      holdT = setTimeout(() => { if (g && !g.moved && pointers.size === 1) startPointing(at); }, 350);
    }
    const base = kind === "share" ? (st.shareOwn || { zoom: st.host.sz || 1, tx: 0, ty: 0 }) : st.follow ? { ...st.host, zoom: 1, tx: 0, ty: 0 } : st.own;
    const pts = [...pointers.values()];
    g = { kind, moved: false, x: e.clientX, y: e.clientY, zoom: base.zoom || 1, tx: base.tx || 0, ty: base.ty || 0, yaw: base.yaw ?? 40,
      dist: pts.length > 1 ? Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) : 0, w: cvLayer.clientWidth || 1 };
  });
  stage.addEventListener("pointermove", (e) => {
    if (mine && e.pointerId === mine.pointerId) { e.preventDefault(); moveMine(e); return; }
    if (pointing && e.pointerId === pointing.pointerId) { e.preventDefault(); movePointing(e); return; }
    if (priceHold && e.pointerId === priceHold.id) {
      if (Math.hypot(e.clientX - priceHold.x, e.clientY - priceHold.y) > 6) { clearTimeout(holdT); priceHold = null; } // she is scrolling
      return;
    }
    if (!g || !pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.values()];
    const dx = e.clientX - g.x, dy = e.clientY - g.y;
    if (!g.moved && Math.hypot(dx, dy) < 6 && pts.length < 2) return;
    if (!g.moved) { g.moved = true; clearTimeout(holdT); if (st.follow && g.kind !== "share") explore("gesture"); }
    e.preventDefault();
    // On his tab her pinch and drag are hers alone, to read something small; it is not exploring.
    if (g.kind === "share") {
      const o = st.shareOwn || (st.shareOwn = { zoom: g.zoom, tx: 0, ty: 0 });
      if (pts.length > 1) {
        const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        if (!g.dist) g.dist = d;
        o.zoom = Math.max(1, Math.min(4, g.zoom * (d / g.dist)));
      } else if (o.zoom > 1) { o.tx = g.tx + dx; o.ty = g.ty + dy; }
      applyShareZoom();
      return;
    }
    if (g.kind === "tour") {
      st.own.yaw = Math.max(0, Math.min(100, g.yaw - (dx / g.w) * 70));
      paintYaw(cvLayer, st.own.yaw);
      return;
    }
    if (pts.length > 1) {
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      if (!g.dist) g.dist = d;
      st.own.zoom = Math.max(1, Math.min(4, g.zoom * (d / g.dist)));
    } else if ((st.own.zoom || 1) > 1) {
      st.own.tx = g.tx + dx;
      st.own.ty = g.ty + dy;
    }
    applyView(cvLayer, canvas, st.own, st.own);
  });
  const up = (e) => {
    clearTimeout(holdT);
    if (priceHold && e.pointerId === priceHold.id) priceHold = null;
    if (mine && e.pointerId === mine.pointerId) endMine();
    if (pointing && e.pointerId === pointing.pointerId) stopPointing();
    pointers.delete(e.pointerId);
    if (!g) return;
    if (pointers.size === 0) {
      if (g.moved && g.kind !== "tour" && g.kind !== "share") zoomSignal();
      if (g.moved && g.kind === "tour") status();
      g = null;
    } else {
      const pts = [...pointers.values()];
      g = { ...g, x: pts[0].x, y: pts[0].y, zoom: st.own?.zoom || 1, tx: st.own?.tx || 0, ty: st.own?.ty || 0, dist: 0 };
    }
  };
  stage.addEventListener("pointerup", up);
  stage.addEventListener("pointercancel", up);

  // ------------------------------------------------------------------------------------------ messages --
  function onData(payload, participant, _kind, topic) {
    if (!participant || !participant.identity) return;
    // Ink and pointers come from anyone allowed; everything else from the host alone.
    if (topic === TOPIC.PEER_PTR) { if (roleOf(participant) !== "host") onPeerPointer(decode(payload), participant); return; }
    if (topic === TOPIC.INK_LIVE || topic === TOPIC.INK_OP) {
      const b = decode(payload);
      if (!b || !mayDraw(participant, holders)) return;
      if (topic === TOPIC.INK_LIVE) onInkLive(b, participant.identity);
      else onInkOp(b, participant.identity);
      return;
    }
    if (roleOf(participant) !== "host") return;
    const body = decode(payload);
    if (!body) return;
    if (topic === TOPIC.PEN) { onPen(body); return; }
    if (topic === TOPIC.STATE) {
      const before = st.host.scene;
      const sz = Math.max(1, Math.min(3, +body.sz || 1));
      if (sz !== (st.host.sz || 1)) st.shareOwn = null; // he zoomed: she follows his again
      const camWas = st.host.cam || null;
      st.host = { scene: body.scene, variant: body.variant, spot: body.spot, zoom: body.zoom || 1, room: body.room, yaw: body.yaw ?? 40, pitch: body.pitch ?? 0, vid: body.vid || null, sz,
        cam: typeof body.cam === "string" ? body.cam.slice(0, 80) : null };
      applyShareZoom();
      st.vidAt = performance.now();
      if (!canvas.byId[st.host.scene] && !isPage(st.host.scene)) st.host.scene = scenes[0].id;
      if (st.face && before !== st.host.scene) toast(t("showing", st.lang, { host: hostFirst, scene: pickLang(sceneOf(canvas, st.host.scene).name, st.lang) }));
      if (st.follow && st.host.cam === camWas) paint();
      else paintVideo(); // the site camera came on or went off: the stage's picture changes
    } else if (topic === TOPIC.PTR) {
      // His pointer: a point on a page, a direction in the 360°, or a place in a row of the price page (W3).
      st.ptr = body && Number.isFinite(body.x) ? { x: body.x, y: body.y } : body && Number.isFinite(body.u) ? { u: body.u, v: body.v }
        : body && Number.isInteger(body.row) && Number.isFinite(body.fx) && Number.isFinite(body.fy) ? { row: body.row, fx: body.fx, fy: body.fy } : null;
      if (!st.face) paintPointer(st.share ? shareLayer : cvLayer, st.share || st.follow ? st.ptr : null);
    } else if (topic === TOPIC.ASK) {
      if (body.ask === "follow" && !st.follow) askCard(`${hostFirst} would like to show you something`, `You're exploring on your own. Follow ${hostFirst} to see what he is showing.`, followAgain, `Follow ${hostFirst}`, "Keep exploring");
      if (body.ask === "status") status(); // he has just rejoined and asks where everyone is
      if (body.ask === "tour" && st.face) askCard(`${hostFirst} would like to show you something`, "The tour is one tap away.", () => setFace(false), "See the tour", `Stay with ${hostFirst}`);
    } else if (topic === TOPIC.ACK) {
      if (body.done === "sent") toast(`${hostFirst} is sending the cost sheet to your WhatsApp`);
    }
  }
  room
    .on(RoomEvent.DataReceived, onData)
    .on(RoomEvent.ParticipantConnected, (p) => { if (roleOf(p) === "host") { status(); toast(`${p.name || hostFirst} joined`); } paintVideo(); })
    .on(RoomEvent.ParticipantDisconnected, (p) => {
      // When he leaves, every pen comes back; her pointer and strokes from anyone who left go with them.
      if (roleOf(p) === "host" && holders.size) onPen({ holders: [] });
      // The stage he chose leaves with him: on his return it waits for his word, or the site camera flashed up (re-audit S1).
      if (roleOf(p) === "host") st.host.cam = null;
      peers.delete(p.identity);
      for (const [id, l] of live) if (l.by === p.identity) live.delete(id);
      paintVideo();
    })
    .on(RoomEvent.TrackSubscribed, paintVideo)
    .on(RoomEvent.TrackUnsubscribed, paintVideo)
    // When he stops sharing, "unsubscribed" arrives while the publication still holds its track; only "unpublished"
    // comes after it is gone — without it her screen kept showing the tab (found by the share e2e, W3).
    .on(RoomEvent.TrackUnpublished, paintVideo)
    .on(RoomEvent.TrackMuted, paintVideo)
    .on(RoomEvent.TrackUnmuted, paintVideo)
    .on(RoomEvent.LocalTrackPublished, paintVideo)
    .on(RoomEvent.LocalTrackUnpublished, paintVideo)
    .on(RoomEvent.ActiveSpeakersChanged, paintVideo)
    // g-site-privacy (RM-hosted only): who may take her tracks follows the room — a site phone her voice only, never
    // her camera. On today's meeting privacy.guard does nothing: no permissions are ever set.
    .on(RoomEvent.ParticipantConnected, privacy.guard)
    .on(RoomEvent.ParticipantDisconnected, privacy.guard)
    .on(RoomEvent.LocalTrackPublished, privacy.guard);
  privacy.guard();

  // ------------------------------------------------------------------------------------------ attention --
  // Time on each part of the story, reported every 20 seconds and when the meeting ends.
  const flush = () => {
    account();
    const rounded = Object.fromEntries(Object.entries(st.dwell).map(([k, v]) => [k, Math.round(v)]).filter(([, v]) => v > 0));
    const d = JSON.stringify(rounded);
    if (d === st.sentDwell) return;
    st.sentDwell = d;
    postEvents(code, [{ t: "dwell", d: rounded }]);
  };
  const report = setInterval(flush, 15000);
  const onHide = () => { if (document.visibilityState === "hidden") flush(); };
  document.addEventListener("visibilitychange", onHide);
  const onResize = () => { fitPlans(cvLayer); paint(); };
  window.addEventListener("resize", onResize);

  paintVideo();
  paintOverlays();
  status();
  session.ended.then(() => {
    clearInterval(report);
    clearTimeout(tidy);
    room.unregisterTextStreamHandler(TOPIC.INK);
    room.unregisterByteStreamHandler(TOPIC.STILL);
    for (const x of canvas.stills.values()) URL.revokeObjectURL(x.src);
    canvas.stills.clear();
    document.removeEventListener("visibilitychange", onHide);
    flush();
    window.removeEventListener("resize", onResize);
    document.body.classList.remove("ms-dark");
  });
}
